import React, { useEffect, useState } from "react";
import {
  Box, Typography, TextField, Button, Grid,
  FormControl, InputLabel, Select, MenuItem, OutlinedInput,
  FormLabel, RadioGroup, FormControlLabel, Radio,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { useIntl } from "react-intl";

import {
  createHumanCapitalGoal,
  createHumanCapitalIssue,
  fetchJobs,
  fetchBusinessProcesses,
  fetchStrategicGoals,
  fetchCustomerValues,
  fetchFinancialGoals,
  fetchStrategicIssues,
  fetchHumanCapitalGoals,
  fetchInfoCapitalGoalsApp,
  fetchInfoCapitalGoalsIt,
  fetchInfoCapitalGoalsData,
} from "../../api/companyApi";
import { useNotification } from "../../providers/NotificationProvider";
import { useAuth } from "../../providers/AuthProvider";

export default function SmHumanCapitalGoalCreatePage() {
  const { businessDomainId } = useParams();
  const navigate = useNavigate();
  const intl = useIntl();
  const notify = useNotification();
  const { companyId } = useAuth();
  const t = (id) => intl.formatMessage({ id });

  const [kpiType, setKpiType] = useState("job"); // "job" | "businessProcess"
  const [successFactorName, setSuccessFactorName] = useState("");
  const [successFactorDescription, setSuccessFactorDescription] = useState("");

  const [selectedJob, setSelectedJob] = useState(null);
  const [selectedNames, setSelectedNames] = useState([]);
  const [jobs, setJobs] = useState([]);

  const [selectedBusinessProcessId, setSelectedBusinessProcessId] = useState("");
  const [businessProcesses, setBusinessProcesses] = useState([]);

  const [sourceIds, setSourceIds] = useState([]);
  const [strategicGoals, setStrategicGoals] = useState([]);
  const [customerValues, setCustomerValues] = useState([]);
  const [financialGoals, setFinancialGoals] = useState([]);
  const [strategicIssues, setStrategicIssues] = useState([]);
  const [humanCapitalGoals, setHumanCapitalGoals] = useState([]);
  const [infoCapitalGoalsApp, setInfoCapitalGoalsApp] = useState([]);
  const [infoCapitalGoalsIt, setInfoCapitalGoalsIt] = useState([]);
  const [infoCapitalGoalsData, setInfoCapitalGoalsData] = useState([]);
  const [usedIndicatorNames, setUsedIndicatorNames] = useState(new Set());
  const [bpSelectedIndicatorNames, setBpSelectedIndicatorNames] = useState([]);

  const sourceIndicators = selectedJob?.indicators || [];

  // BP mode: collect unique indicators from all selected humanCapitalGoal sources
  const bpSourceIndicators = kpiType === "businessProcess"
    ? [...new Map(
        sourceIds
          .flatMap((id) => {
            const hcg = humanCapitalGoals.find((g) => g.successFactorId === id);
            return hcg?.indicators || [];
          })
          .map((ind) => [ind.indicatorName, ind])
      ).values()]
    : [];

  useEffect(() => {
    (async () => {
      try {
        const [jbs, bps, sgGoals, cvGoals, fgGoals, issues, icApp, icIt, icData, existingSfs] = await Promise.all([
          fetchJobs(businessDomainId),
          fetchBusinessProcesses(businessDomainId).catch(() => []),
          fetchStrategicGoals(businessDomainId).catch(() => []),
          fetchCustomerValues(businessDomainId).catch(() => []),
          fetchFinancialGoals(businessDomainId).catch(() => []),
          fetchStrategicIssues(businessDomainId).catch(() => []),
          fetchInfoCapitalGoalsApp(businessDomainId).catch(() => []),
          fetchInfoCapitalGoalsIt(businessDomainId).catch(() => []),
          fetchInfoCapitalGoalsData(businessDomainId).catch(() => []),
          fetchHumanCapitalGoals(businessDomainId).catch(() => []),
        ]);
        setJobs(Array.isArray(jbs) ? jbs : []);
        setBusinessProcesses(Array.isArray(bps) ? bps : []);
        setStrategicGoals(Array.isArray(sgGoals) ? sgGoals : []);
        setCustomerValues(Array.isArray(cvGoals) ? cvGoals : []);
        setFinancialGoals(Array.isArray(fgGoals) ? fgGoals : []);
        setStrategicIssues(Array.isArray(issues) ? issues : []);
        setInfoCapitalGoalsApp(Array.isArray(icApp) ? icApp : []);
        setInfoCapitalGoalsIt(Array.isArray(icIt) ? icIt : []);
        setInfoCapitalGoalsData(Array.isArray(icData) ? icData : []);
        setHumanCapitalGoals(Array.isArray(existingSfs) ? existingSfs : []);
        const used = new Set(
          (Array.isArray(existingSfs) ? existingSfs : [])
            .flatMap((sf) => sf.indicators || [])
            .map((ind) => ind.indicatorName)
            .filter(Boolean)
        );
        setUsedIndicatorNames(used);
      } catch (e) {
        notify.apiError(e);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessDomainId]);

  const handleJobChange = (e) => {
    const job = jobs.find((j) => j.jobId === e.target.value);
    setSelectedJob(job || null);
    setSelectedNames([]);
  };

  const handleKpiTypeChange = (e) => {
    setKpiType(e.target.value);
    setSelectedJob(null);
    setSelectedNames([]);
    setSelectedBusinessProcessId("");
    setSourceIds([]);
    setBpSelectedIndicatorNames([]);
  };

  const handleBpSourceChange = (e) => {
    setSourceIds(e.target.value);
    setBpSelectedIndicatorNames([]); // reset indicators when sources change
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (kpiType === "job") {
      if (sourceIndicators.length > 0 && selectedNames.length === 0) {
        notify.error(t("financialGoal.indicatorRequired"));
        return;
      }
    } else {
      if (sourceIds.length === 0) {
        notify.error(t("orgCapitalGoal.sources.required") || "ソース（人的資本目標）を1件以上選択してください");
        return;
      }
      if (bpSelectedIndicatorNames.length === 0) {
        notify.error(t("financialGoal.indicatorRequired"));
        return;
      }
    }
    try {
      if (kpiType === "businessProcess") {
        const bpIndicatorsCheck = bpSelectedIndicatorNames
          .map((name) => bpSourceIndicators.find((ind) => ind.indicatorName === name))
          .filter(Boolean);
        if (bpIndicatorsCheck.length === 0) {
          notify.error(t("financialGoal.indicatorRequired"));
          return;
        }
      }
      if (kpiType === "job") {
        const allGoals = [
          ...strategicGoals, ...customerValues, ...financialGoals,
          ...strategicIssues, ...humanCapitalGoals,
          ...infoCapitalGoalsApp, ...infoCapitalGoalsIt, ...infoCapitalGoalsData,
        ];
        const sources = sourceIds
          .map((id) => {
            const goal = allGoals.find((s) => s.successFactorId === id);
            if (!goal) return null;
            return { successFactorId: goal.successFactorId, successFactorName: goal.successFactorName };
          })
          .filter(Boolean);
        const indicators = selectedNames
          .map((name) => sourceIndicators.find((ind) => ind.indicatorName === name))
          .filter(Boolean);
        await createHumanCapitalGoal({
          successFactorName,
          successFactorDescription,
          jobId: selectedJob?.jobId || "",
          indicators,
          sources,
          businessDomainId,
          companyId,
        });
      } else {
        // BP mode: sources must be HumanCapitalGoal type only
        const sources = sourceIds
          .map((id) => {
            const goal = humanCapitalGoals.find((g) => g.successFactorId === id);
            if (!goal) return null;
            return { successFactorId: goal.successFactorId, successFactorName: goal.successFactorName };
          })
          .filter(Boolean);
        const indicators = bpSelectedIndicatorNames
          .map((name) => bpSourceIndicators.find((ind) => ind.indicatorName === name))
          .filter(Boolean);
        await createHumanCapitalIssue({
          successFactorName,
          successFactorDescription,
          businessProcessId: selectedBusinessProcessId,
          tasks: [],
          indicators,
          sources,
          businessDomainId,
          companyId,
        });
      }
      notify.success(t("message.create.success"));
      navigate(`/design/${businessDomainId}/strategy-map/human-capital-goals`, { replace: true });
    } catch (err) {
      notify.apiError(err);
    }
  };

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 2 }}>
        {t("humanCapitalGoal.create.title")}
      </Typography>

      <Box component="form" onSubmit={handleSubmit}>
        <Grid container spacing={2}>

          {/* KPI種類選択 */}
          <Grid size={12}>
            <FormControl component="fieldset">
              <FormLabel component="legend">{t("humanCapitalGoal.title")}</FormLabel>
              <RadioGroup row value={kpiType} onChange={handleKpiTypeChange}>
                <FormControlLabel value="job" control={<Radio />} label={t("job.title")} />
                <FormControlLabel value="businessProcess" control={<Radio />} label={t("businessProcess.title")} />
              </RadioGroup>
            </FormControl>
          </Grid>

          <Grid size={12}>
            <TextField
              fullWidth required
              label={t("successFactor.name")}
              value={successFactorName}
              onChange={(e) => setSuccessFactorName(e.target.value)}
            />
          </Grid>

          <Grid size={12}>
            <TextField
              fullWidth multiline rows={3}
              label={t("successFactor.description")}
              value={successFactorDescription}
              onChange={(e) => setSuccessFactorDescription(e.target.value)}
            />
          </Grid>

          {/* ジョブ選択（ジョブKPIのみ） */}
          {kpiType === "job" && jobs.length > 0 && (
            <Grid size={12}>
              <FormControl fullWidth>
                <InputLabel>{t("job.title")}</InputLabel>
                <Select
                  value={selectedJob?.jobId || ""}
                  label={t("job.title")}
                  onChange={handleJobChange}
                >
                  {jobs.map((j) => (
                    <MenuItem key={j.jobId} value={j.jobId}>
                      {j.jobName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}

          {/* ビジネスプロセス選択（BPKPIのみ） */}
          {kpiType === "businessProcess" && (
            <Grid size={12}>
              <FormControl fullWidth>
                <InputLabel>{t("businessProcess.title")}</InputLabel>
                <Select
                  value={selectedBusinessProcessId}
                  label={t("businessProcess.title")}
                  onChange={(e) => setSelectedBusinessProcessId(e.target.value)}
                >
                  {businessProcesses.map((bp) => (
                    <MenuItem key={bp.businessProcessId} value={bp.businessProcessId}>
                      {bp.businessProcessName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}

          {/* ソース選択（ジョブモード：全種類のゴール） */}
          {kpiType === "job" && (
            <Grid size={12}>
              <FormControl fullWidth>
                <InputLabel>{t("orgCapitalGoal.sources.label")}</InputLabel>
                <Select
                  multiple
                  value={sourceIds}
                  onChange={(e) => setSourceIds(e.target.value)}
                  input={<OutlinedInput label={t("orgCapitalGoal.sources.label")} />}
                >
                  {financialGoals.length > 0 && [
                    <MenuItem key="__fg_header" disabled sx={{ fontWeight: 700, opacity: 1, color: "text.primary" }}>
                      ── {t("financialGoal.list.title")} ──
                    </MenuItem>,
                    ...financialGoals.map((item) => (
                      <MenuItem key={item.successFactorId} value={item.successFactorId}>
                        {item.successFactorName}
                      </MenuItem>
                    )),
                  ]}
                  {customerValues.length > 0 && [
                    <MenuItem key="__cv_header" disabled sx={{ fontWeight: 700, opacity: 1, color: "text.primary" }}>
                      ── {t("customerValue.list.title")} ──
                    </MenuItem>,
                    ...customerValues.map((item) => (
                      <MenuItem key={item.successFactorId} value={item.successFactorId}>
                        {item.successFactorName}
                      </MenuItem>
                    )),
                  ]}
                  {strategicGoals.length > 0 && [
                    <MenuItem key="__sg_header" disabled sx={{ fontWeight: 700, opacity: 1, color: "text.primary" }}>
                      ── {t("strategicGoal.list.title")} ──
                    </MenuItem>,
                    ...strategicGoals.map((item) => (
                      <MenuItem key={item.successFactorId} value={item.successFactorId}>
                        {item.successFactorName}
                      </MenuItem>
                    )),
                  ]}
                  {strategicIssues.length > 0 && [
                    <MenuItem key="__si_header" disabled sx={{ fontWeight: 700, opacity: 1, color: "text.primary" }}>
                      ── {t("strategicIssue.list.title")} ──
                    </MenuItem>,
                    ...strategicIssues.map((item) => (
                      <MenuItem key={item.successFactorId} value={item.successFactorId}>
                        {item.successFactorName}
                      </MenuItem>
                    )),
                  ]}
                  {humanCapitalGoals.length > 0 && [
                    <MenuItem key="__hcg_header" disabled sx={{ fontWeight: 700, opacity: 1, color: "text.primary" }}>
                      ── {t("humanCapitalGoal.list.title")} ──
                    </MenuItem>,
                    ...humanCapitalGoals.map((item) => (
                      <MenuItem key={item.successFactorId} value={item.successFactorId}>
                        {item.successFactorName}
                      </MenuItem>
                    )),
                  ]}
                  {infoCapitalGoalsApp.length > 0 && [
                    <MenuItem key="__ica_header" disabled sx={{ fontWeight: 700, opacity: 1, color: "text.primary" }}>
                      ── {t("infoCapitalGoalApp.list.title")} ──
                    </MenuItem>,
                    ...infoCapitalGoalsApp.map((item) => (
                      <MenuItem key={item.successFactorId} value={item.successFactorId}>
                        {item.successFactorName}
                      </MenuItem>
                    )),
                  ]}
                  {infoCapitalGoalsIt.length > 0 && [
                    <MenuItem key="__icit_header" disabled sx={{ fontWeight: 700, opacity: 1, color: "text.primary" }}>
                      ── {t("infoCapitalGoalIt.list.title")} ──
                    </MenuItem>,
                    ...infoCapitalGoalsIt.map((item) => (
                      <MenuItem key={item.successFactorId} value={item.successFactorId}>
                        {item.successFactorName}
                      </MenuItem>
                    )),
                  ]}
                  {infoCapitalGoalsData.length > 0 && [
                    <MenuItem key="__icd_header" disabled sx={{ fontWeight: 700, opacity: 1, color: "text.primary" }}>
                      ── {t("infoCapitalGoalData.list.title")} ──
                    </MenuItem>,
                    ...infoCapitalGoalsData.map((item) => (
                      <MenuItem key={item.successFactorId} value={item.successFactorId}>
                        {item.successFactorName}
                      </MenuItem>
                    )),
                  ]}
                </Select>
              </FormControl>
            </Grid>
          )}

          {/* ソース選択（BPモード：人的資本目標のみ） */}
          {kpiType === "businessProcess" && (
            <Grid size={12}>
              <FormControl fullWidth required>
                <InputLabel>{t("humanCapitalGoal.list.title")}</InputLabel>
                <Select
                  multiple
                  value={sourceIds}
                  onChange={handleBpSourceChange}
                  input={<OutlinedInput label={t("humanCapitalGoal.list.title")} />}
                >
                  {humanCapitalGoals.length === 0 ? (
                    <MenuItem disabled>{t("humanCapitalGoal.list.title")}が存在しません</MenuItem>
                  ) : (
                    humanCapitalGoals.map((item) => (
                      <MenuItem key={item.successFactorId} value={item.successFactorId}>
                        {item.successFactorName}
                      </MenuItem>
                    ))
                  )}
                </Select>
              </FormControl>
            </Grid>
          )}

          {/* 指標選択（ジョブモードのみ） */}
          {kpiType === "job" && (
            <Grid size={12}>
              <FormControl fullWidth>
                <InputLabel>{t("financialGoal.selectIndicatorHint")}</InputLabel>
                <Select
                  multiple
                  disabled={sourceIndicators.length === 0}
                  value={selectedNames}
                  onChange={(e) => setSelectedNames(e.target.value)}
                  input={<OutlinedInput label={t("financialGoal.selectIndicatorHint")} />}
                >
                  {sourceIndicators.map((ind) => (
                    <MenuItem key={ind.indicatorName} value={ind.indicatorName} disabled={usedIndicatorNames.has(ind.indicatorName)}>
                      {ind.indicatorName}（{ind.unit}）{usedIndicatorNames.has(ind.indicatorName) ? " （登録済み）" : ""}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}

          {/* 指標選択（BPモード：選択したHumanCapitalGoalの指標） */}
          {kpiType === "businessProcess" && (
            <Grid size={12}>
              <FormControl fullWidth>
                <InputLabel>{t("financialGoal.selectIndicatorHint")}</InputLabel>
                <Select
                  multiple
                  disabled={bpSourceIndicators.length === 0}
                  value={bpSelectedIndicatorNames}
                  onChange={(e) => setBpSelectedIndicatorNames(e.target.value)}
                  input={<OutlinedInput label={t("financialGoal.selectIndicatorHint")} />}
                >
                  {bpSourceIndicators.length === 0 ? (
                    <MenuItem disabled>人的資本目標を選択すると指標が表示されます</MenuItem>
                  ) : (
                    bpSourceIndicators.map((ind) => (
                      <MenuItem key={ind.indicatorName} value={ind.indicatorName}>
                        {ind.indicatorName}（{ind.unit}）
                      </MenuItem>
                    ))
                  )}
                </Select>
              </FormControl>
            </Grid>
          )}

        </Grid>

        <Box sx={{ mt: 3, display: "flex", gap: 2 }}>
          <Button variant="contained" type="submit" disabled={!successFactorName.trim()}>
            {t("common.add")}
          </Button>
          <Button
            variant="outlined"
            onClick={() => navigate(`/design/${businessDomainId}/strategy-map/human-capital-goals`)}
          >
            {t("common.cancel")}
          </Button>
        </Box>
      </Box>
    </Box>
  );
}
